import React, { FC, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
	DeviceType,
	Lightbox,
	Orientation,
	Platform,
	Settings,
	WriteDirection,
} from 'sofa-reborn-react';
import { IDexfSettings, ITranslations } from '@inter-ikea-kompis/types';
import { RootState, AppDispatch } from '../store';
import SaveView from '../components/SaveView';
import {
	ConfirmationSummaryShareDesignStateEnum,
	ConfigurationSummaryConfirmationCardTypeEnum,
} from '@inter-ikea-kompis/component-configuration-summary';
import SendByEmailEnum from '@inter-ikea-kompis/component-send-by-email/lib/enums/SendByEmailStateEnum';
import { backToShareMenu, copyCode, saveConfiguration, sendEmail } from '../store/checkout/actions';
import { NotificationService } from '@inter-ikea-kompis/services';
import { NotificationLinkTypeEnum } from '@inter-ikea-kompis/enums';
import { setCurrentLightbox } from '../store/application/actions';
import {
	ShareDesignCardStateEnum,
	ShareDesignCardCopyStateEnum,
} from '@inter-ikea-kompis/component-share-design-card';

const SaveViewContainer: FC<{
	saveVpcState: ConfirmationSummaryShareDesignStateEnum;
	confirmationCardType: ConfigurationSummaryConfirmationCardTypeEnum | null;
	qrCodeUrl: string;
}> = (props) => {
	const platform = useSelector<RootState, Platform>((state) => state.deviceInfo.platform);
	const deviceType = useSelector<RootState, DeviceType>((state) => state.deviceInfo.deviceType);
	const orientation = useSelector<RootState, Orientation>((state) => state.deviceInfo.orientation);
	const writeDirection = useSelector<RootState, WriteDirection>(
		(state) => state.localization.writeDirection
	);
	const currentLightbox = useSelector<RootState, Lightbox>(
		(state) => state.application.currentLightbox
	);
	const settings = useSelector<RootState, Settings>((state) => state.settings.settings);
	const kompisSettings = useSelector<RootState, IDexfSettings | null>(
		(state) => state.settings.kompisSettings
	);
	const kompisTranslations = useSelector<RootState, ITranslations | null>(
		(state) => state.localization.kompisTranslations
	);
	const vpcCode = useSelector<RootState, string>((state) => state.checkout.vpcCode as string);
	const storeId = useSelector<RootState, string>((state) => state.localization.storeId as string);
	const saveVpcState = props.saveVpcState;
	const confirmationCardType = props.confirmationCardType;
	const dispatch = useDispatch<AppDispatch>();

	const [emailValue, setEmailValue] = useState('');
	const [emailState, setEmailState] = useState(SendByEmailEnum.default);
	const [invalidEmailInput, setInvalidEmailInput] = useState(false);
	const [selectedState, setSelectedState] = useState(ShareDesignCardStateEnum.default);
	const [copyDesignCodeState, setCopyDesignCodeState] = useState(
		ShareDesignCardCopyStateEnum.default
	);
	const [copyDesignLinkState, setCopyDesignLinkState] = useState(
		ShareDesignCardCopyStateEnum.default
	);

	const onEmailInput = (e: CustomEvent): void => {
		setEmailValue(e.detail.value);
	};

	const onEmailValidation = (e: CustomEvent): void => {
		setInvalidEmailInput(e.detail.invalidInput);
	};

	async function onSendEmail(e: CustomEvent): Promise<void> {
		dispatch(saveConfiguration());
		setEmailState(SendByEmailEnum.loading);

		const notificationService = new NotificationService({
			locale: settings.locale,
			localizationPrefixApplication: settings.localizationPrefixApplication,
			localizationEndpoints: settings.localizationEndpoints,
		});

		const email = e.detail.value;
		dispatch(sendEmail());
		try {
			await notificationService.sendEmail({
				vpcCode,
				email,
				linkType: NotificationLinkTypeEnum.pickinglist,
				storeId,
			});
			setEmailState(SendByEmailEnum.success);
		} catch (error) {
			setEmailState(SendByEmailEnum.failed);
		}
	}

	function onResetEmail(): void {
		setEmailValue('');
		setEmailState(SendByEmailEnum.default);
	}

	const onCloseSaveForLater = (): void => {
		dispatch(setCurrentLightbox('none'));
	};

	function onSelect(e: CustomEvent): void {
		setSelectedState(e.detail.selectedState);
	}

	function onCopyDesignCodeClick(e: CustomEvent): void {
		setCopyDesignCodeState(e.detail.state);
		dispatch(copyCode());
	}

	function onCopyDesignCodeReset(): void {
		setCopyDesignCodeState(ShareDesignCardCopyStateEnum.default);
	}

	function onCopyDesignLinkClick(e: CustomEvent): void {
		setCopyDesignLinkState(e.detail.state);
	}

	function onCopyDesignLinkReset(): void {
		setCopyDesignLinkState(ShareDesignCardCopyStateEnum.default);
	}

	function onBackButtonClick(): void {
		dispatch(backToShareMenu(selectedState));
		setSelectedState(ShareDesignCardStateEnum.default);
	}

	if (!kompisSettings || !kompisTranslations) {
		return null;
	}

	return (
		<SaveView
			platform={platform}
			deviceType={deviceType}
			orientation={orientation}
			writeDirection={writeDirection}
			vpcCode={vpcCode}
			kompisSettings={kompisSettings}
			kompisTranslations={kompisTranslations}
			locale={settings.locale}
			confirmationCardType={confirmationCardType}
			saveVpcState={saveVpcState}
			emailValue={emailValue}
			invalidEmailInput={invalidEmailInput}
			emailState={emailState}
			qrCodeUrl={props.qrCodeUrl}
			showSaveForLater={currentLightbox === 'save-for-later'}
			selectedState={selectedState}
			copyDesignCodeState={copyDesignCodeState}
			copyDesignLinkState={copyDesignLinkState}
			disableSmsAndEmail={platform === 'kiosk' && storeId === '705'} // SP-4332
			onEmailInput={onEmailInput}
			onEmailValidation={onEmailValidation}
			onSendEmail={onSendEmail}
			onResetEmail={onResetEmail}
			onClose={onCloseSaveForLater}
			onSelect={onSelect}
			onCopyDesignCodeClick={onCopyDesignCodeClick}
			onCopyDesignCodeReset={onCopyDesignCodeReset}
			onCopyDesignLinkClick={onCopyDesignLinkClick}
			onCopyDesignLinkReset={onCopyDesignLinkReset}
			onBackButtonClick={onBackButtonClick}
		></SaveView>
	);
};
export default SaveViewContainer;
